<template>
  <naj-box
    v-if="isHeadersFetched"
    :no-header="noHeader"
    :title="najBoxTitle"
    :error-message="errorMessage"
    :has-footer="pagination"
    :no-bottom-margin="noMarginBottom"
    :disable-collapsing="!showCollapsing"
    :collapsed="collapsed"
    no-gutters
  >
    <template
      v-if="!noHeader"
      v-slot:header
    >
      <slot name="header" />
    </template>

    <v-data-table
      v-if="!(hideWhenNoItems && !castedItems.length)"
      ref="select"
      locale="ar"
      :class="{
        'disable-items-per-page': disableItemsPerPage,
        'table-max-height': !!maxHeight,
        'not-fetched-for-first-time':
          !(this.items || []).length && !loading && !isFetchedForFirstTime,
      }"
      :style="{ maxHeight: maxHeight }"
      :headers="tableHeaders"
      :items="castedItems"
      :items-per-page="!disableItemsPerPage && pagination ? itemsPerPage : castedItems.length"
      :loading-text="local.common.loading"
      :server-items-length="
        !disableItemsPerPage && pagination ? serverItemsLength : castedItems.length
      "
      :footer-props="footerProps"
      :no-data-text="noDataText || local.common.noData"
      :show-expand="showExpand"
      :single-expand="true"
      v-bind="{ loading, hideDefaultFooter: pagination, showSelect }"
      must-sort
      @page-count="pageCount = $event"
      @update:items-per-page="$emit('update:items-per-page', $event)"
      @input="$emit('input', $event)"
      @update:sort-by="sortByEventCallBack"
      @update:sort-desc="sortDirectionEventCallBack"
      @item-expanded="$emit('expandItem', $event)"
    >
      <template
        v-for="(header, index) in tableHeaders"
        v-slot:[itemPropertySlot(header.value)]="{ item }"
      >
        <v-tooltip
          :key="index"
          top
          :disabled="!header.columnTooltipMessage || !header.columnTooltipMessage(item)"
        >
          <template v-slot:activator="{ on }">
            <div
              v-if="header.value"
              :key="index"
              :class="[
                {
                  'column-style': item.columnsStyles ? item.columnsStyles[header.value] : null,
                },
                item.columnsStyles ? item.columnsStyles[header.value] : '',
                header.columnClass?header.columnClass(item[header.value]):'',
              ]"
              v-on="on"
            >
              {{ header.formatter?header.formatter(item[header.value]):item[header.value] }}
            </div>
          </template>
          <span v-html="header.columnTooltipMessage?header.columnTooltipMessage(item): null" />
        </v-tooltip>
      </template>

      <template
        v-if="showExpand"
        v-slot:item.data-table-expand="{ item, isExpanded, expand }"
      >
        <v-icon
          v-if="item.isExpandable"
          @click="expand(!isExpanded)"
        >
          {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        </v-icon>
      </template>

      <template v-slot:item.action="{ item, header, key }">
        <slot
          :item="items[item.originalItemIndex]"
          :header="header"
          :index="item.originalItemIndex"
        />
      </template>
      <template
        v-if="showExpand"
        v-slot:expanded-item="{ item }"
      >
        <td :colspan="headers.length">
          <slot :item="item" />
        </td>
      </template>
      <template
        v-if="showSelect && !!$scopedSlots.headerCheckbox"
        v-slot:header.data-table-select="{ props, on }"
      >
        <slot
          name="headerCheckbox"
          v-bind="props"
          v-on="on"
        />
      </template>
      <template
        v-if="showSelect && !!$scopedSlots.itemsCheckbox"
        v-slot:item.data-table-select="attrs"
      >
        <slot
          name="itemsCheckbox"
          v-bind="attrs"
        />
      </template>
    </v-data-table>
    <template v-slot:footer>
      <div
        v-if="pagination"
        class="pagination-container"
      >
        <v-pagination
          :value="pageIndex + 1"
          :length="paginationLength"
          :total-visible="totalVisible"
          @input="$emit('update:pageIndex', $event - 1)"
        ></v-pagination>
        <naj-input
          v-if="paginationLength > totalVisible"
          label=""
          :type-id="formFieldType.selectbox"
          :value="pageIndex + 1"
          :items="paginationValues"
          :clearable="false"
          @change="$event && $emit('update:pageIndex', $event - 1)"
        ></naj-input>
      </div>
    </template>
  </naj-box>
</template>

<script>
export default {
  name: 'NajDataTable',
  props: {
    noHeader: { type: Boolean },
    headers: { required: true },
    items: { required: true },
    itemsPerPage: {
      type: Number,
      default: 10,
      required: false,
    },
    pageIndex: {
      type: Number,
      default: 0,
      required: false,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    serverItemsLength: {
      type: Number,
      required: false,
      default: -1,
    },
    disableItemsPerPage: {
      type: Boolean,
      required: false,
      default: false,
    },
    pagination: {
      type: Boolean,
      required: false,
    },
    showSelect: {
      type: Boolean,
      required: false,
    },
    noDataText: {
      type: String,
      required: false,
    },
    noMarginBottom: {
      type: Boolean,
      required: false,
      default: true,
    },
    title: {
      type: String,
      required: false,
    },
    errorMessage: {
      type: String,
      required: false,
    },
    showExpand: {
      type: Boolean,
      required: false,
    },
    showCollapsing: {
      type: Boolean,
      default: false,
    },
    hideWhenNoItems: {
      type: Boolean,
      default: false,
    },
    maxHeight: { type: String },
    collapsed: { type: Boolean },
    displayTime: {
      type: Boolean,
      default: false,
      required: false,
    },
    skipCastingKeys: {
      type: Array,
      default: null,
    },
    showTotalServerCount: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      totalVisible: 7,
      isFetchedForFirstTime: false,
      sortOptions: {
        sortBy: null,
        sortDirection: null,
      },
    };
  },
  computed: {
    isHeadersFetched() {
      return !!this.tableHeaders && !!this.tableHeaders.length;
    },
    footerProps() {
      const props = {
        itemsPerPageText: this.local.common.rowsPerPage,
        showCurrentPage: true,
      };
      if (this.disableItemsPerPage) {
        props.itemsPerPageText = '';
        props.disableItemsPerPage = true;
      }
      return props;
    },
    tableHeaders() {
      return this.headers
        .filter(item => item.value && !item.hidden)
        .filter(i => !(!this.showExpand && i.value === 'data-table-expand'))
        .map((i) => {
          const item = i;
          item.sortable = item.sortable ? item.sortable : false;

          if (item.value === 'action') {
            item.sortable = false;
            item.align = item.align || 'center';
          }
          return item;
        });
    },
    paginationLength() {
      if (!this.serverItemsLength || this.serverItemsLength <= 0) return 0;
      return Math.ceil(this.serverItemsLength / this.itemsPerPage);
    },
    paginationValues() {
      return Array.from({ length: this.paginationLength || 0 }, (_, i) => i + 1);
    },
    castedItems() {
      const items = JSON.parse(JSON.stringify(this.items || []));
      return items.map((obj, index) => {
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            // to fix casting items to number/date we should
            // add the excluded key to the skipCastingKeys array
            if ((this.skipCastingKeys || []).some(sk => key == sk)) {
              continue;
            }
            if (
              isNaN(+obj[key]) &&
              isNaN(Date.parse(obj[key])) == false &&
              !Array.isArray(obj[key]) &&
              !/[\u0600-\u06FF\p{White_Space}]/u.test(obj[key])
            ) {
              if (Date.parse(obj[key]) >= -3471300309000) {
                obj[key] = this.displayTime ? this.$options.filters.hijriDateTime(obj[key]) : this.$options.filters.hijriDate(obj[key]);
              }
              obj[key] = this.$options.filters.hindiNumbers(obj[key]);
            }
          }
        }
        return {
          ...obj,
          originalItemIndex: index,
        };
      });
    },
    najBoxTitle() {
      let title = this.showTotalServerCount ?
        (this.loading ? '...' : (' : ' + this.serverItemsLength)) : '';
      return this.title + title;
    },
  },
  watch: {
    loading: {
      immediate: true,
      handler(newVal, oldVal) {
        this.isFetchedForFirstTime = oldVal !== undefined;
      },
    },
    $route() {
      this.isFetchedForFirstTime = false;
    },
  },
  methods: {
    toggleSelectAll(value) {
      this.$refs.select.toggleSelectAll(value);
    },
    itemPropertySlot(property) {
      return `item.${property}`;
    },
    sortByEventCallBack(sortBy) {
      this.sortOptions.sortBy = sortBy[0] || null;
    },
    sortDirectionEventCallBack(sortDesc) {
      this.sortOptions.sortDirection = sortDesc[0] ? 'DESC' : 'ASC';
      this.emitSortEvent();
    },
    emitSortEvent() {
      if (!this.sortOptions.sortBy) {
        return;
      }

      this.$emit('on-sort-change', this.sortOptions);
    },
  },
};
</script>

<style lang="scss" scoped>
.not-fetched-for-first-time ::v-deep tbody {
  display: none;
}
.column-style {
  background-color: #eee;
  padding: 5px 18px 7px;
  color: #333;
  line-height: 1;
  border-radius: 100px;
  font-weight: 700;
  font-size: 13px;
  text-align: center;
  &.default-state {
    background-color: #f6f6f6;
    color: #a3a3a3;
  }
  &.transfered-reconciliation {
    background-color: #e1eeff;
    color: #7f9bc2;
  }
  &.registered {
    background-color: #e4dfdb;
    color: #94867d;
  }
  &.completed {
    background-color: #dcf9d7;
    color: #0db55d;
  }
  &.accepted-from-audit {
    background-color: #ecfae9;
    color: #6ab685;
  }
  &.under-review {
    background-color: #ffe9c6;
    color: #b5740d;
  }
  &.return-to-audit {
    background-color: #f6f0e5;
    color: #6a5e4b;
  }
  &.canceled {
    background-color: #fdf4ed;
    color: #f68887;
  }
  &.transfered-subcircle {
    background-color: #dbf4f0;
    color: #94b4ae;
  }
  &.not-completed {
    background-color: #ffe8e8;
    color: #db4a4a;
  }
  &.new {
    background-color: #f5f5f5;
    color: #a1a1a1;
  }
  &.canceled-not-reviewed {
    background-color: #fdf4ed;
    color: #f68887;
  }
  &.case-corona {
    background: transparent;
    color: #db4a4a;
    &:after {
      display: inline-block;
      font: normal normal normal 24px/1 "Material Design Icons";
      content: "\F13B6";
    }
  }

  &.warning-alert {
    background: transparent;
    display: flex;
    &:before {
      color: orange;
      font: normal normal normal 24px/1 "Material Design Icons";
      content: "\F0026";
    }
  }
  &.warning-timer {
    background: transparent;
    display: flex;
    float: left;
    &:after {
      color: orange;
      font: normal normal normal 24px/1 "Material Design Icons";
      content: "\F051B";
    }
  }
  &.danger-timer {
    background: transparent;
    display: flex;
    float: left;
    &:after {
      color: red;
      font: normal normal normal 24px/1 "Material Design Icons";
      content: "\F051B";
    }
  }
  &.checked-request {
    background: transparent;
    color: #b5740d;
    &:after {
      display: inline-block;
      font: normal normal normal 24px/1 "Material Design Icons";
      content: "\F0BE2";
    }
  }
  &.negative {
    background-color: #fdf4ed;
    color: #f68887;
  }
  &.positive {
    background-color: #ecfae9;
    color: #6ab685;
  }
  &.attention {
    background-color: #ffe9c6;
    color: #b5740d;
  }
  &.urgent-request {
    background: transparent;
    font-weight: 400;
    &:before {
      display: inline-block;
      font: normal normal normal 18px/1 "Material Design Icons";
      content: "\F023B";
      color: #f68887;
    }
  }
  &.negative-urgent {
    &:before {
      display: inline-block;
      font: normal normal normal 18px/1 "Material Design Icons";
      content: "\F023B";
      color: #f68887;
    }
  }
  &.positive-urgent {
    &:before {
      display: inline-block;
      font: normal normal normal 18px/1 "Material Design Icons";
      content: "\F023B";
      color: #6ab685;
    }
  }
  &.attention-urgent {
    &:before {
      display: inline-block;
      font: normal normal normal 18px/1 "Material Design Icons";
      content: "\F023B";
      color: #b5740d;
    }
  }
  &.default-urgent {
    &:before {
      display: inline-block;
      font: normal normal normal 18px/1 "Material Design Icons";
      content: "\F023B";
      color: #a3a3a3;
    }
  }
}
.pagination-container {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  .input-container {
    width: 100px;
    ::v-deep .v-input {
      margin: 0;
      .v-input__slot {
        margin: 0;
      }
      &.v-select.v-input--is-focused input {
        min-width: 48px;
      }
      .v-text-field__details {
        display: none;
      }
    }
  }
  ::v-deep .v-pagination {
    .v-pagination__item,
    .v-pagination__navigation {
      border: 2px solid #d0d3db;
      border-radius: 50%;
      -webkit-box-shadow: none;
      box-shadow: none;
      height: 42px;
      width: 42px;
      line-height: 40px;
      font-size: 16px;
      color: #9ea1b3;
      &.v-pagination__item--active {
        color: #ffffff !important;
      }
    }
    .v-pagination__navigation {
      margin: 0 10px;
      .v-icon {
        font-size: 24px;
      }
    }
  }
}
.disable-items-per-page ::v-deep {
  .v-data-footer,
  .v-data-footer__select,
  .v-data-footer__pagination {
    display: none;
  }
}
.table-max-height {
  overflow: auto;
}

.change-type-deleted {
  color: #f68887;
}
.change-type-modified {
  color: #6ab685;
}
</style>
